import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Menu, Search, Bell, User, LogOut, Settings, Shield,
  Gift, ShoppingCart, CreditCard, X, Coins, Trophy, Calendar, MessageCircle
} from "lucide-react";
import LiveChatPanel from "../LiveChatPanel";
import SupportPanel from "../SupportPanel";
import LanguageToggle from "../LanguageToggle";
import PlaythroughProgress from "../PlaythroughProgress";

const API = window.location.origin;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_dc2ea8e9-88c1-4191-a09e-3c70ac39f874/artifacts/lxlb74jo_IMG_9876.jpeg";

// Avatar image URLs mapping
const AVATAR_IMAGES = {
  cool_guy: "/avatars/cool_guy.png",
  lucky_lady: "/avatars/lucky_lady.png",
  mystery_man: "/avatars/mystery_man.png",
  golden_king: "/avatars/golden_king.png",
  lion: "/avatars/lion.png",
  wolf: "/avatars/wolf.png",
  robot: "/avatars/robot.png",
  phoenix: "/avatars/phoenix.png",
  dragon: "/avatars/dragon.png",
  queen: "/avatars/queen.png",
  ninja: "/avatars/ninja.png",
  diamond_skull: "/avatars/diamond_skull.png",
  dice_queen: "/avatars/dice_queen.png",
  card_queen: "/avatars/card_queen.png",
  casino_boss: "/avatars/casino_boss.png",
  holiday_girls: "/avatars/holiday_girls.png",
  lucky_leprechaun: "/avatars/lucky_leprechaun.png",
  chip_queen: "/avatars/chip_queen.png",
  magic_genie: "/avatars/magic_genie.png",
  ace_lady: "/avatars/ace_lady.png",
  red_king: "/avatars/red_king.png",
  purple_king: "/avatars/purple_king.png",
  dark_dealer: "/avatars/dark_dealer.png",
};

// Reusable Avatar with XP Ring component
const AvatarXPRing = ({ user, size = "sm", hasUnread = false }) => {
  const xpProgress = ((user?.xp || 0) / Math.max((user?.level || 1) * 100, 1));
  const circumference = 2 * Math.PI * 21; // r=21

  const sizeMap = {
    xs: { outer: "w-10 h-10", inner: "w-8 h-8", badge: "w-4 h-4 text-[8px] -bottom-0.5 -right-0.5", iconSize: "w-4 h-4", inset: "-inset-[4px]", viewbox: "w-[calc(100%+8px)] h-[calc(100%+8px)]", strokeW: "2.5" },
    sm: { outer: "w-11 h-11", inner: "w-9 h-9", badge: "w-5 h-5 text-[9px] -bottom-1 -right-1", iconSize: "w-5 h-5", inset: "-inset-[5px]", viewbox: "w-[calc(100%+10px)] h-[calc(100%+10px)]", strokeW: "2.5" },
    md: { outer: "w-14 h-14", inner: "w-11 h-11", badge: "w-6 h-6 text-[10px] -bottom-1 -right-1", iconSize: "w-5 h-5", inset: "-inset-[6px]", viewbox: "w-[calc(100%+12px)] h-[calc(100%+12px)]", strokeW: "3" },
    lg: { outer: "w-[72px] h-[72px]", inner: "w-14 h-14", badge: "w-7 h-7 text-[11px] -bottom-1 -right-1", iconSize: "w-6 h-6", inset: "-inset-[8px]", viewbox: "w-[calc(100%+16px)] h-[calc(100%+16px)]", strokeW: "3.5" },
  };

  const s = sizeMap[size] || sizeMap.sm;

  return (
    <div className={`relative ${s.outer} flex items-center justify-center`} data-testid={`avatar-xp-ring-${size}`}>
      {/* SVG XP Progress Ring */}
      <svg className={`absolute ${s.inset} ${s.viewbox}`} viewBox="0 0 48 48">
        <circle cx="24" cy="24" r="21" fill="none" stroke="rgba(77,124,255,0.15)" strokeWidth={s.strokeW} />
        <circle
          cx="24" cy="24" r="21"
          fill="none"
          stroke="url(#xpGrad)"
          strokeWidth={s.strokeW}
          strokeLinecap="round"
          strokeDasharray={`${xpProgress * circumference} ${circumference}`}
          transform="rotate(-90 24 24)"
          style={{ filter: 'drop-shadow(0 0 4px rgba(77,124,255,0.6))', transition: 'stroke-dasharray 0.5s ease' }}
        />
        <defs>
          <linearGradient id="xpGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4d7cff" />
            <stop offset="100%" stopColor="#7f5dff" />
          </linearGradient>
        </defs>
      </svg>

      {/* Avatar circle */}
      <div className={`${s.inner} rounded-full bg-gradient-to-br from-[#4d7cff] to-[#7f5dff] flex items-center justify-center overflow-hidden`}>
        {user?.avatar_url ? (
          <img src={user.avatar_url} alt="Avatar" className="w-full h-full object-cover" data-testid="header-user-avatar-custom" />
        ) : user?.avatar && AVATAR_IMAGES[user.avatar] ? (
          <img src={AVATAR_IMAGES[user.avatar]} alt="Avatar" className="w-full h-full object-cover" />
        ) : (
          <User className={`${s.iconSize} text-black`} />
        )}
      </div>

      {/* Level badge */}
      <div
        className={`absolute ${s.badge} rounded-full flex items-center justify-center font-black border-2 border-[#050814]`}
        style={{
          background: 'linear-gradient(135deg, #4d7cff, #7f5dff)',
          boxShadow: '0 0 6px rgba(77,124,255,0.6)',
        }}
      >
        {user?.level || 1}
      </div>

      {/* Direct Message unread indicator (2026-Feb) */}
      {hasUnread && (
        <>
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-rose-500 border-2 border-[#050814] animate-ping" data-testid="avatar-dm-unread-ping" />
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-rose-500 border-2 border-[#050814]" data-testid="avatar-dm-unread-dot" />
        </>
      )}
    </div>
  );
};

const Header = ({ onMenuToggle, onCloseSidebar }) => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSupportPanel, setShowSupportPanel] = useState(false);
  // Direct Message unread indicator (2026-Feb owner directive)
  const [dmUnread, setDmUnread] = useState(false);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/user/dm/unread`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setDmUnread(!!r.data?.has_unread);
      } catch { /* silent */ }
    };
    poll();
    const id = setInterval(poll, 20 * 1000);
    // Refresh on window focus too so returning from another tab is instant.
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    // Listen for a global "dm:read" event dispatched by the Profile
    // Messages tab so the dot clears without waiting for the next poll.
    const onRead = () => setDmUnread(false);
    window.addEventListener("dm:read", onRead);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("dm:read", onRead);
    };
  }, [token]);

  const isAdmin = user?.email === "admin@jackroyals.com" || user?.is_admin || user?.role === "admin";
  const isModerator = user?.is_moderator || user?.role === "moderator";
  const isSeniorMod = user?.is_senior_moderator || user?.role === "senior_moderator";

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unread_count || 0);
      } catch (e) {
        // Use default notifications if endpoint doesn't exist
        setNotifications([
          { id: 1, type: "welcome", title: "Welcome to JackRoyals!", message: "Start playing and win big!", read: false, created_at: new Date().toISOString() },
          { id: 2, type: "bonus", title: "Daily Bonus Available", message: "Claim your free coins now!", read: false, created_at: new Date().toISOString() },
        ]);
        setUnreadCount(2);
      }
    };
    fetchNotifications();
  }, [token]);

  const markAsRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const clearAllNotifications = async () => {
    try {
      await axios.delete(`${API}/api/notifications/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.error("Failed to clear notifications:", e);
    }
    setNotifications([]);
    setUnreadCount(0);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "bonus": return <Gift className="w-4 h-4 text-green-400" />;
      case "win": return <Trophy className="w-4 h-4 text-[#4d7cff]" />;
      case "coins": return <Coins className="w-4 h-4 text-yellow-400" />;
      case "birthday": return <Calendar className="w-4 h-4 text-pink-400" />;
      default: return <Bell className="w-4 h-4 text-blue-400" />;
    }
  };

  return (
    <>
    <header className="sticky top-0 z-40 backdrop-blur-xl" style={{ background: 'rgba(5,8,20,0.95)', borderBottom: '1px solid rgba(120,150,255,0.15)' }}>
      <div className="px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Left: Menu Toggle & Logo — pinned, never scrolls */}
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <button 
              onClick={onMenuToggle}
              className="lg:hidden p-1.5 sm:p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-white/5"
            >
              <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            
            <Link to="/lobby" className="flex items-center gap-2 sm:gap-3">
              <img src={LOGO_URL} alt="JackRoyals" className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl object-cover" />
              <span className="hidden sm:inline text-base sm:text-xl font-bold text-white">
                Jack<span style={{ color: '#4d7cff' }}>Royals</span>
              </span>
            </Link>
          </div>

          {/* Right: Balances & Actions — horizontally scrollable on mobile
              so every pill (SC · Redeemable · GC · Get Coins · Redeem ·
              Chat · Bell) is reachable via swipe. Owner directive Feb 2026. */}
          <div
            className="flex-1 min-w-0 flex items-center gap-1.5 sm:gap-3 overflow-x-auto sm:overflow-visible scrollbar-hide"
            style={{ WebkitOverflowScrolling: 'touch' }}
            data-testid="header-actions-scroll"
          >
            {/* Language toggle hidden until full ES translation lands; toggle JSX kept for easy restore */}
            {false && <LanguageToggle compact className="sm:hidden" />}
            {false && <LanguageToggle className="hidden sm:inline-flex" />}

            {/* SC pill — visible on ALL breakpoints, shrink-0 so it never
                collapses inside the mobile horizontal scroll row. */}
            <div className="shrink-0 flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full">
              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                <span className="text-[9px] sm:text-[10px] font-bold text-white">SC</span>
              </div>
              <span className="font-bold text-emerald-400 text-[11px] sm:text-sm tabular-nums whitespace-nowrap">
                {user?.sweeps_coins?.toFixed(2) || "0.00"} SC
              </span>
            </div>

            {/* Live playthrough / redeemable — visible on all breakpoints
                (mobile just scrolls to it). */}
            <div className="shrink-0">
              <PlaythroughProgress />
            </div>

            {/* GC pill — mobile compresses the number to k/M so it doesn't
                blow the row width; desktop shows the raw number. */}
            <div className="shrink-0 flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-[#4d7cff]/10 border border-[#4d7cff]/30 rounded-full">
              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#4d7cff] flex items-center justify-center">
                <span className="text-[9px] sm:text-[10px] font-bold text-black">GC</span>
              </div>
              <span className="font-bold text-[#4d7cff] text-[11px] sm:text-sm tabular-nums whitespace-nowrap">
                <span className="hidden sm:inline">
                  {user?.gold_coins?.toLocaleString(undefined, {maximumFractionDigits: 0}) || "0"}
                </span>
                <span className="sm:hidden">
                  {(() => {
                    const v = Number(user?.gold_coins || 0);
                    if (v >= 1_000_000) return `${(v/1_000_000).toFixed(1)}M`;
                    if (v >= 1_000)     return `${(v/1_000).toFixed(1)}k`;
                    return String(Math.round(v));
                  })()}
                </span>
              </span>
            </div>

            {/* Mobile: Profile/Logout quick access */}
            {user && (
              <div className="relative sm:hidden shrink-0">
                <button
                  onClick={() => { setShowMobileMenu(!showMobileMenu); setShowNotifications(false); onCloseSidebar?.(); }}
                  data-testid="mobile-profile-btn"
                  className="block"
                >
                  <AvatarXPRing user={user} size="xs" hasUnread={dmUnread} />
                </button>
                {showMobileMenu && (
                  <>
                    <div className="fixed inset-0 z-[45]" onClick={() => setShowMobileMenu(false)} />
                    <div className="absolute right-0 top-full mt-2 w-56 rounded-xl shadow-2xl z-[55] overflow-hidden" style={{ background: '#0a0f1f', border: '1px solid rgba(120,150,255,0.2)' }}>
                      <div className="px-4 py-3 border-b border-white/[0.06]">
                        <div className="flex items-center gap-3 mb-2">
                          <AvatarXPRing user={user} size="md" />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-white text-sm truncate flex items-center gap-1">{user?.username}{(user?.verification_status === "verified" || user?.kyc_verified === true || user?.kyc_status === "approved") && <svg className="w-4 h-4 text-blue-400 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" data-testid="verified-checkmark"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/><circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="2"/></svg>}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{user?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            user?.vip_rank === "Royal" ? "bg-[#4d7cff]/15 text-[#4d7cff]" :
                            user?.vip_rank === "Ruby" ? "bg-red-500/15 text-red-400" :
                            user?.vip_rank === "Diamond" ? "bg-cyan-500/15 text-cyan-300" :
                            user?.vip_rank === "Platinum" ? "bg-purple-500/15 text-purple-300" :
                            user?.vip_rank === "Gold" ? "bg-yellow-500/15 text-[#ffd86b]" :
                            user?.vip_rank === "Silver" ? "bg-zinc-500/15 text-zinc-300" :
                            "bg-[#c47a3a]/15 text-[#c47a3a]"
                          }`}>{user?.vip_rank || "Bronze"}</span>
                          <span className="text-[10px] text-zinc-600">{user?.xp || 0} / {(user?.level || 1) * 100} XP</span>
                        </div>
                      </div>
                      <div className="py-1">
                        <button onClick={() => { navigate("/profile"); setShowMobileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-zinc-300 hover:bg-white/5 text-sm" data-testid="mobile-profile-link">
                          <User className="w-4 h-4" /> Profile
                        </button>
                        <button onClick={() => { navigate("/rewards"); setShowMobileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-zinc-300 hover:bg-white/5 text-sm">
                          <Gift className="w-4 h-4 text-[#4d7cff]" /> Daily Rewards
                        </button>
                        <button onClick={() => { navigate("/verification"); setShowMobileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-zinc-300 hover:bg-white/5 text-sm">
                          <Shield className="w-4 h-4" /> Verification
                        </button>
                        <button onClick={() => { setShowSupportPanel(true); setShowMobileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-zinc-300 hover:bg-white/5 text-sm" data-testid="mobile-support-btn">
                          <MessageCircle className="w-4 h-4 text-blue-400" /> Support
                        </button>
                        {(isAdmin || isSeniorMod) && (
                          <button onClick={() => { navigate("/admin"); setShowMobileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#4d7cff] hover:bg-white/5 text-sm" data-testid="mobile-admin-dashboard-btn">
                            <Settings className="w-4 h-4" /> Admin
                          </button>
                        )}
                        {(isModerator || isAdmin) && (
                          <button
                            onClick={() => { navigate("/moderator"); setShowMobileMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-emerald-400 hover:bg-emerald-500/5 text-sm font-semibold"
                            data-testid="mobile-mod-dashboard-btn"
                          >
                            <Shield className="w-4 h-4" /> Moderator Dashboard
                          </button>
                        )}
                      </div>
                      <div className="border-t border-white/[0.06] py-1">
                        <button onClick={() => { logout(); setShowMobileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-red-400 hover:bg-red-500/10 text-sm font-medium" data-testid="mobile-logout-btn">
                          <LogOut className="w-4 h-4" /> Log Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Wagering Progress - Only show if user has bonus */}
            {user?.bonus_gc > 0 && user?.bonus_wagering_required > 0 && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-full cursor-pointer group relative">
                <Trophy className="w-4 h-4 text-purple-400" />
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(100, (user.bonus_wagering_progress / user.bonus_wagering_required) * 100)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-purple-400 font-bold">
                      {Math.round((user.bonus_wagering_progress / user.bonus_wagering_required) * 100)}%
                    </span>
                  </div>
                </div>
                {/* Tooltip on hover */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-zinc-900 border border-purple-500/30 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                  <p className="text-xs text-white font-bold mb-1">Bonus Unlock Progress</p>
                  <p className="text-[10px] text-zinc-400">
                    Wagered: {user.bonus_wagering_progress?.toFixed(2)} / {user.bonus_wagering_required?.toFixed(2)}
                  </p>
                  <p className="text-[10px] text-purple-400 mt-1">
                    Complete wagering to unlock {user.bonus_gc?.toFixed(2)} GC
                  </p>
                </div>
              </div>
            )}

            {/* Get Coins Button — permanently pinned on all breakpoints */}
            <Link to="/store" className="shrink-0" data-testid="header-get-coins-link">
              <Button
                className="text-white font-bold rounded-full hover:opacity-90 transition-opacity flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-sm px-3 py-1.5 sm:px-4 sm:py-2 whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #4d7cff, #7f5dff)', boxShadow: '0 0 12px rgba(77,124,255,0.5)' }}
              >
                <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Get Coins</span>
              </Button>
            </Link>

            {/* Redeem Button — now visible on mobile via horizontal scroll */}
            <Link to="/redeem" className="shrink-0" data-testid="header-redeem-link">
              <Button 
                variant="outline"
                className="font-bold rounded-full text-[11px] sm:text-sm px-3 py-1.5 sm:px-4 sm:py-2 whitespace-nowrap"
                style={{ borderColor: 'rgba(77,124,255,0.5)', color: '#4d7cff' }}
              >
                Redeem
              </Button>
            </Link>

            {/* Live Chat Button — now visible on mobile via horizontal scroll */}
            <button
              onClick={() => setShowLiveChat(true)}
              className="shrink-0 flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full hover:bg-emerald-500/20 transition-colors whitespace-nowrap"
              data-testid="header-live-chat-btn"
            >
              <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" />
              <span className="text-[11px] sm:text-sm font-bold text-emerald-400">Chat</span>
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-emerald-500 rounded-full animate-pulse"></span>
            </button>

            {/* Notifications - hidden on mobile */}
            <div className="relative hidden md:block shrink-0">
              <button 
                onClick={() => { setShowNotifications(!showNotifications); setShowMobileMenu(false); onCloseSidebar?.(); }}
                className="p-1.5 sm:p-2 text-zinc-400 hover:text-white rounded-full hover:bg-white/5 relative"
                data-testid="notification-bell"
              >
                <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 sm:top-0 sm:right-0 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold text-white">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <>
                  {/* Backdrop */}
                  <div 
                    className="fixed inset-0 z-[45]" 
                    onClick={() => setShowNotifications(false)}
                  />
                  
                  {/* Dropdown */}
                  <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 rounded-xl shadow-2xl z-[55] max-h-[70vh] overflow-hidden" style={{ background: '#0a0f1f', border: '1px solid rgba(120,150,255,0.2)' }}>
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-white/10">
                      <h3 className="font-bold text-white">Notifications</h3>
                      <div className="flex items-center gap-2">
                        {notifications.length > 0 && (
                          <button 
                            onClick={clearAllNotifications}
                            className="text-xs text-zinc-400 hover:text-white"
                          >
                            Clear all
                          </button>
                        )}
                        <button 
                          onClick={() => setShowNotifications(false)}
                          className="p-1 text-zinc-400 hover:text-white"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Notification List */}
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-zinc-500">
                          <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No notifications</p>
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <div 
                            key={notif.id}
                            onClick={() => markAsRead(notif.id)}
                            className={`p-4 border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${
                              !notif.read ? "bg-[#4d7cff]/5" : ""
                            }`}
                          >
                            <div className="flex gap-3">
                              <div className="mt-1">
                                {getNotificationIcon(notif.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-white text-sm truncate">{notif.title}</p>
                                  {!notif.read && (
                                    <span className="w-2 h-2 bg-[#4d7cff] rounded-full flex-shrink-0"></span>
                                  )}
                                </div>
                                <p className="text-zinc-400 text-xs mt-0.5 line-clamp-2">{notif.message}</p>
                                <p className="text-zinc-600 text-[10px] mt-1">
                                  {new Date(notif.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Footer */}
                    <div className="p-3 border-t border-white/10">
                      <Link 
                        to="/profile" 
                        onClick={() => setShowNotifications(false)}
                        className="block text-center text-[#4d7cff] hover:text-[#6d99ff] text-sm font-medium"
                      >
                        View All Notifications
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Search - Desktop only */}
            <button 
              onClick={() => navigate("/lobby?search=1")}
              className="hidden md:block p-2 text-zinc-400 hover:text-white rounded-full hover:bg-white/5"
              data-testid="header-search-btn"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Profile Dropdown with Level Ring - hidden on mobile (available in bottom nav) */}
            <div className="relative group hidden md:block">
              <button className="flex items-center gap-2 p-1 sm:p-1.5 rounded-full hover:bg-white/5 transition-colors" data-testid="header-profile-trigger">
                <AvatarXPRing user={user} size="sm" hasUnread={dmUnread} />
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute right-0 top-full mt-2 w-64 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[55]" style={{ background: '#0a0f1f', border: '1px solid rgba(120,150,255,0.2)' }}>
                {/* Level & XP Section */}
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-center gap-3 mb-3">
                    {/* Avatar with XP Ring in dropdown */}
                    <AvatarXPRing user={user} size="md" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-white truncate flex items-center gap-1">{user?.username}{(user?.verification_status === "verified" || user?.kyc_verified === true || user?.kyc_status === "approved") && <svg className="w-4 h-4 text-blue-400 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" data-testid="verified-checkmark-mobile"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/><circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="2"/></svg>}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{user?.email}</p>
                    </div>
                  </div>

                  {/* Tier */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      user?.vip_rank === "Royal" ? "bg-[#4d7cff]/20 text-[#4d7cff]" :
                      user?.vip_rank === "Ruby" ? "bg-red-500/20 text-red-400" :
                      user?.vip_rank === "Diamond" ? "bg-cyan-500/20 text-cyan-300" :
                      user?.vip_rank === "Platinum" ? "bg-purple-500/20 text-purple-300" :
                      user?.vip_rank === "Gold" ? "bg-yellow-500/20 text-[#ffd86b]" :
                      user?.vip_rank === "Silver" ? "bg-zinc-500/20 text-zinc-300" :
                      "bg-[#c47a3a]/20 text-[#c47a3a]"
                    }`}>{user?.vip_rank || "Bronze"} Tier</span>
                    <span className="text-[10px] text-zinc-600">Level {user?.level || 1}</span>
                  </div>

                  {/* XP Bar */}
                  <div className="w-full h-2 rounded-full overflow-hidden mb-1" style={{ background: '#1a2340', border: '1px solid rgba(120,150,255,0.15)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(((user?.xp || 0) / Math.max((user?.level || 1) * 100, 1)) * 100, 100)}%`,
                        background: 'linear-gradient(90deg, #4d7cff, #7f5dff)',
                        boxShadow: '0 0 8px rgba(77,124,255,0.5)',
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-zinc-600">{user?.xp || 0} / {(user?.level || 1) * 100} XP &bull; Next reward: 50,000 GC + 0.5 SC</p>
                </div>

                {/* Nav Links */}
                <div className="p-2">
                  <Link to="/profile" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-zinc-300 hover:text-white">
                    <User className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                  <Link to="/verification" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-zinc-300 hover:text-white">
                    <Shield className="w-4 h-4" />
                    <span>Verification</span>
                  </Link>
                  <button onClick={() => setShowSupportPanel(true)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-zinc-300 hover:text-white" data-testid="desktop-support-btn">
                    <MessageCircle className="w-4 h-4 text-blue-400" />
                    <span>Support</span>
                  </button>
                  <Link to="/referral" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-zinc-300 hover:text-white">
                    <Gift className="w-4 h-4" />
                    <span>Referral Program</span>
                  </Link>
                  {(isAdmin || isSeniorMod) && (
                    <Link to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-[#4d7cff]" data-testid="desktop-admin-dashboard-link">
                      <Settings className="w-4 h-4" />
                      <span>Admin Dashboard</span>
                    </Link>
                  )}
                  {(isModerator || isAdmin) && (
                    <Link
                      to="/moderator"
                      className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-emerald-500/10 transition-colors text-emerald-400 font-semibold"
                      data-testid="desktop-mod-dashboard-link"
                    >
                      <Shield className="w-4 h-4" />
                      <span>Moderator Dashboard</span>
                    </Link>
                  )}
                  <button 
                    onClick={logout} 
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    {/* Live Chat Panel */}
    {showLiveChat && (
      <LiveChatPanel onClose={() => setShowLiveChat(false)} />
    )}

    {/* Support Panel */}
    <SupportPanel isOpen={showSupportPanel} onClose={() => setShowSupportPanel(false)} />
  </>
  );
};

export default Header;
